import { ShieldCheck, Users, Layers, BadgePercent } from "lucide-react";

const HIGHLIGHTS = [
  {
    icon: ShieldCheck,
    title: "Operators have skin in the game",
    text: "Because the operator funds 60% of every outlet from their own capital, their incentives are fully aligned with yours. They only profit when the outlet performs.",
  },
  {
    icon: Users,
    title: "A curated 40% investor pool",
    text: "The remaining 40% is opened to a maximum of 200 verified individuals, hand-selected by frantiger — never an open, unlimited crowd. Fractions start at ₹5,000.",
  },
  {
    icon: Layers,
    title: "Debt-first capital stack",
    text: "Your 40% is deployed through senior private debt instruments (CCDs / NCDs), collateralised against the outlet — so investors sit ahead of operator equity on returns.",
  },
  {
    icon: BadgePercent,
    title: "Transparent, flat fees",
    text: "frantiger charges a 5% Annual Management Charge (AMC) for structuring, vetting and reporting — plus a flat ₹100 platform fee on each secondary Offer to Buy / Sell trade. Nothing hidden.",
  },
];

export default function CapitalRatio() {
  return (
    <section id="capital-ratio" data-testid="capital-ratio-section" className="bg-slate-900 py-24">
      <div className="mx-auto max-w-6xl px-4">
        <div className="grid items-center gap-14 lg:grid-cols-[0.95fr_1.05fr]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400">The 60:40 capital rule</p>
            <h2 className="font-heading mt-4 text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Every outlet is funded 60% by the operator, 40% by you
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-300">
              fraction never asks investors to carry an outlet alone. The franchise owner /
              operator commits <span className="font-bold text-white">60% of the total project
              cost</span> as their own equity. The other <span className="font-bold text-orange-400">
              40%</span> is raised from a pool of at most{" "}
              <span className="font-bold text-white">200 verified individuals</span> chosen by
              frantiger, in debentures from just ₹5,000 — a structure designed to keep operator
              accountability high and investor downside protected.
            </p>

            <div className="mt-8 rounded-[2rem] bg-slate-800/60 p-6" data-testid="capital-ratio-bar">
              <div className="flex h-6 overflow-hidden rounded-full">
                <div className="flex items-center justify-center bg-slate-500 text-[11px] font-bold text-white" style={{ width: "60%" }}>
                  60% Operator
                </div>
                <div className="progress-glow flex items-center justify-center bg-orange-500 text-[11px] font-bold text-white" style={{ width: "40%" }}>
                  40% Pool
                </div>
              </div>
              <div className="mt-3 flex justify-between text-[11px] font-bold text-slate-400">
                <span>Operator equity · skin in the game</span>
                <span className="text-orange-400">Investor pool · max 200 members</span>
              </div>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {HIGHLIGHTS.map(({ icon: Icon, title, text }, i) => (
              <div key={title} className="glass-dark rounded-3xl p-6" data-testid={`capital-highlight-${i + 1}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-lg shadow-orange-500/40">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="font-heading mt-4 text-base font-bold text-white">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
